import React from 'react';
import { Link } from 'react-router-dom';

const CK_LOGO = 'https://www.cloud-kinetics.com/wp-content/uploads/2023/07/CloudKinetics-Logo.svg';

export default function LandingPage() {
  return (
    <div className="landing">
      <header className="landing-header">
        <div className="landing-header-inner">
          <div className="landing-brand">
            <img src={CK_LOGO} alt="Cloud Kinetics" className="landing-ck-logo" />
            <span className="landing-brand-sub">Partner Network</span>
          </div>
          <div className="landing-header-actions">
            <Link to="/request-access" className="landing-btn-outline">Request Access</Link>
            <Link to="/login" className="landing-btn-primary">Partner Login</Link>
          </div>
        </div>
      </header>

      <section className="landing-hero">
        <div className="landing-hero-inner">
          <div className="hero-eyebrow">Authorised Partner Portal</div>
          <h1>Manage your pipeline<br />with Cloud Kinetics</h1>
          <p className="hero-sub">
            A lightweight CRM built for CK partners. Submit opportunities, track every deal stage,
            collaborate with the presales team and close together.
          </p>
          <div className="hero-cta-row">
            <Link to="/login" className="hero-btn-primary">Partner Login →</Link>
            <Link to="/request-access" className="hero-btn-ghost">Become a Partner</Link>
          </div>
        </div>
        <div className="hero-visual" aria-hidden>
          <div className="hero-bars">
            {[55, 80, 45, 90, 65, 75, 50].map((h, i) => (
              <div key={i} className="hero-bar" style={{ height: `${h}%`, animationDelay: `${i * 0.08}s` }} />
            ))}
          </div>
        </div>
      </section>

      <section className="landing-features">
        <div className="landing-inner">
          <p className="section-eyebrow">What you get</p>
          <h2 className="section-title">A full deal lifecycle in one place</h2>
          <div className="features-grid">
            {[
              {
                icon: '📋',
                title: 'Submit Opportunities',
                desc: 'Register customer deals with structured intake. Capture customer details, services, ARR and deal classification in one form.'
              },
              {
                icon: '📊',
                title: 'Pipeline Dashboard',
                desc: 'See your entire portfolio at a glance — total ARR, deal stages, open vs. closed. Know exactly where each opportunity stands.'
              },
              {
                icon: '🗂',
                title: 'Kanban Pipeline',
                desc: 'Visualise deals across stages: Discovery → Qualified → Proposal Validation → FR Submission → PO Received → Delivery → Launched.'
              },
              {
                icon: '💬',
                title: 'Comments & Updates',
                desc: 'Exchange notes and status updates with the CK presales team, all linked to the deal — no emails, no lost context.'
              },
              {
                icon: '📁',
                title: 'Document Management',
                desc: 'Attach RFPs, proposals, contracts and supporting files securely to each opportunity. Access them any time.'
              },
              {
                icon: '🔒',
                title: 'Secure & Invite-Only',
                desc: 'Your deals are visible only to you and the CK team. Invite-only access ensures no cross-partner data leakage.'
              },
            ].map((f, i) => (
              <div key={i} className="feature-card">
                <div className="feature-icon">{f.icon}</div>
                <h3>{f.title}</h3>
                <p>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-how">
        <div className="landing-inner">
          <p className="section-eyebrow">Getting started</p>
          <h2 className="section-title">How it works</h2>
          <div className="steps-row">
            {[
              {
                n: '01',
                title: 'Request Access',
                desc: 'Submit a partner access request. The CK team will review and send your invitation within 2 business days.'
              },
              {
                n: '02',
                title: 'Activate Account',
                desc: 'Accept your email invitation and set your password to activate your secure partner account.'
              },
              {
                n: '03',
                title: 'Submit Your Deals',
                desc: 'Log in and register your customer opportunities with deal details, services required and supporting documents.'
              },
              {
                n: '04',
                title: 'Close Together',
                desc: 'CK engages your customer. Track progress on the kanban board and collaborate via comments at every stage.'
              },
            ].map((s, i) => (
              <div key={i} className="step-card">
                <div className="step-number">{s.n}</div>
                <h3>{s.title}</h3>
                <p>{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="landing-cta-section">
        <div className="landing-inner landing-cta-inner">
          <h2>Ready to grow with Cloud Kinetics?</h2>
          <p>Join our partner network and start co-selling with one of Southeast Asia's leading AWS partners.</p>
          <div className="hero-cta-row">
            <Link to="/request-access" className="hero-btn-primary">Request Partner Access</Link>
            <Link to="/login" className="hero-btn-ghost">Already a partner? Sign in</Link>
          </div>
        </div>
      </section>

      <footer className="landing-footer">
        <div className="landing-footer-inner">
          <div className="landing-footer-brand">
            <img src={CK_LOGO} alt="Cloud Kinetics" className="landing-footer-logo" />
            <span>Partner Network</span>
          </div>
          <span className="landing-footer-copy">© {new Date().getFullYear()} Cloud Kinetics Pte Ltd. All rights reserved.</span>
        </div>
      </footer>
    </div>
  );
}
